<template>
  <h4 class="accordion-header" id="headingFour">
    <button
      type="button"
      class="accordion-button collapsed d-flex flex-row justify-content-between gap-2 stm-btn-accordion-apple-pay"
      data-bs-toggle="collapse"
      data-bs-target="#accordion-apple-pay"
      aria-expanded="false"
      aria-controls="accordion-apple-pay"
    >
      <div class="d-flex justify-content-between w-100">
        Apple Pay
        <img alt="Apple Pay supported" class="icon" :src="appleStoreLogoSvg" />
      </div>
    </button>
  </h4>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <div
    id="accordion-apple-pay"
    class="accordion-collapse collapse"
    aria-labelledby="headingOne"
    data-bs-parent="#paymentAccordion"
  >
    <div class="accordion-body card-element row">
      <PaymentErrorAlert payment-method-name="applepay" :method-error="panel.methodError" :partial-success="panel.partialSuccess" />
      <small class="text-muted my-2 text-center">
        Apple Pay will convert the charge to match your Apple Wallet's billing currency.
        The currency shown at checkout may differ from what appears on your invoice.
      </small>
      <div class="accordion-body card-element row justify-content-center">
        <div
          class="apple-pay-button-with-text apple-pay-button-black-with-text"
          @click="handleApplePayClick"
        >
        </div>
        <PaymentSecurityBadges />
      </div>
    </div>
  </div>
</template>
<script setup>
import { onMounted, watch } from 'vue';
import { capturePaymentError } from '@/services/sentry';
import { useCheckoutStore } from '@/stores/checkout';
import { useGeoIPStore } from '@/stores/geoip';
import PaymentErrorAlert from '@/components/PaymentErrorAlert.vue';
import PaymentSecurityBadges from '@/components/PaymentSecurityBadges.vue';

import appleStoreLogoSvg from '@/assets/svg/apple-store-logo.svg';

const checkoutStore = useCheckoutStore();
const geoIPStore = useGeoIPStore();
const { paymentState, resetPaymentErrors, handlePanelToken } = checkoutStore;
const panel = checkoutStore.panelState.applePay;

const props = defineProps({
  disableButton: Boolean,
  recurlyPricing: {
    type: Object,
    required: true,
  },
  plan: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'hideApplePay',
  'showApplePayVuelidate',
  'paymentProcessing',
  'paymentFailed',
]);

let applePay;

const handleApplePayClick = async () => {
  if (props.disableButton) {
    emit('showApplePayVuelidate');
    return;
  }
  resetPaymentErrors();
  try {
    await applePay.begin();
  } catch (err) { // this is most typically a network issue
    panel.methodError = err?.message || (typeof err === 'string' ? err : 'Apple Pay checkout failed');
  }
};

const initializeApplePay = async () => {
  applePay = recurly.ApplePay({
    braintree: {
      clientAuthorization: import.meta.env.VITE_BRAINTREE_KEY,
      webAuthentication: true,
    },
    country: 'US',
    label: props.plan,
    pricing: props.recurlyPricing,
    // we can't change this dynamically, so let's just assume normal cases
    currency: geoIPStore.details.currency,
  });

  applePay.on('error', err => {
    if (err.name === 'apple-pay-not-available') {
      panel.methodError = 'Apple Pay is not configured on this device.';
      return;
    } else if (err.name !== 'apple-pay-payment-failure' && err.name?.startsWith('apple-pay')) {
      emit('hideApplePay');
      return;
    }
    capturePaymentError('apple_pay_tokenize', err);
    panel.methodError = 'Apple Pay was not able to process the transaction. Please use a different payment method or try again later.';
  });

  applePay.on('token', async token => {
    emit('paymentProcessing');
    await handlePanelToken('applePay', token);
  });
};

watch(() => paymentState.purchaseFailed || paymentState.purchasePartiallySucceeded, (hasFailed) => {
  if (hasFailed) {
    emit('paymentFailed');
  }
});

onMounted(async () => {
  if (!window.recurly || !window.braintree) {
    return;
  }
  try {
    await initializeApplePay();
  } catch (err) {
    panel.methodError = 'Failed to initialize Apple Pay. Please try another payment method.';
  }
});
</script>
<style scoped>
@supports (-webkit-appearance: -apple-pay-button) {
    .apple-pay-button-with-text {
        display: inline-block;
        -webkit-appearance: -apple-pay-button;
        -apple-pay-button-type: check-out;
    }
    .apple-pay-button-with-text > * {
        display: none;
    }
    .apple-pay-button-black-with-text {
        -apple-pay-button-style: black;
    }
    .apple-pay-button-white-with-text {
        -apple-pay-button-style: white;
    }
    .apple-pay-button-white-with-line-with-text {
        -apple-pay-button-style: white-outline;
    }
}


@supports not (-webkit-appearance: -apple-pay-button) {
    .apple-pay-button-with-text {
        --apple-pay-scale: 1; /* (height / 32) */
        display: inline-flex;
        justify-content: center;
        font-size: 12px;
        border-radius: 5px;
        padding: 0px;
        box-sizing: border-box;
        min-width: 200px;
        min-height: 32px;
        max-height: 64px;
    }
    .apple-pay-button-black-with-text {
        background-color: black;
        color: white;
    }
    .apple-pay-button-white-with-text {
        background-color: white;
        color: black;
    }
    .apple-pay-button-white-with-line-with-text {
        background-color: white;
        color: black;
        border: .5px solid black;
    }
    .apple-pay-button-with-text.apple-pay-button-black-with-text > .logo {
        background-image: -webkit-named-image(apple-pay-logo-white);
        background-color: black;
    }
    .apple-pay-button-with-text.apple-pay-button-white-with-text > .logo {
        background-image: -webkit-named-image(apple-pay-logo-black);
        background-color: white;
    }
    .apple-pay-button-with-text.apple-pay-button-white-with-line-with-text > .logo {
        background-image: -webkit-named-image(apple-pay-logo-black);
        background-color: white;
    }
    .apple-pay-button-with-text > .text {
        font-family: -apple-system;
        font-size: calc(1em * var(--apple-pay-scale));
        font-weight: 300;
        align-self: center;
        margin-right: calc(2px * var(--apple-pay-scale));
    }
    .apple-pay-button-with-text > .logo {
        width: calc(35px * var(--scale));
        height: 100%;
        background-size: 100% 60%;
        background-repeat: no-repeat;
        background-position: 0 50%;
        margin-left: calc(2px * var(--apple-pay-scale));
        border: none;
    }
}
</style>
